import { inject } from '@angular/core';
import { OpenAPIV3 } from 'openapi-types';
import { AppModel, DEV_CONFIG } from '../../shared/models/config.model';
import {
  ApiCollectionExportFormat,
  ApiCollectionFile,
  ApiCollectionVariables,
  defaultApiCollectionVariables,
  OpenApiCollectionParameter,
  OpenApiCollectionRequest,
  OpenApiCollectionRequestBody,
} from '../models/api-collection.model';
import {
  PostmanAuth,
  PostmanBody,
  PostmanCollection,
  PostmanHeader,
  PostmanRequestItem,
  PostmanUrl,
} from '../models/postman-collection.model';
import {
  buildExportFileName,
  getBaseUrl,
  getCollectionName,
  getOpenApiCollectionRequests,
  getResource,
} from '../utils/open-api-collection-export.utils';
import { ApiCollectionService } from './api-collection-export.service';

export class PostmanApiCollectionService implements ApiCollectionService {
  readonly format: ApiCollectionExportFormat = ApiCollectionExportFormat.Postman;

  private readonly config = inject(DEV_CONFIG);

  export(app: AppModel, document: OpenAPIV3.Document): ApiCollectionFile {
    const name = getCollectionName(app, document);
    const variables = defaultApiCollectionVariables;
    const requests = getOpenApiCollectionRequests(app, document);
    const collection = this.buildCollection(
      name,
      getBaseUrl(app, document),
      getResource(app, document),
      requests,
      variables,
    );

    return {
      fileName: buildExportFileName(name, '.postman_collection.json'),
      contentType: 'application/json',
      content: JSON.stringify(collection, null, 2),
    };
  }

  private buildCollection(
    name: string,
    baseUrl: string,
    resource: string,
    requests: OpenApiCollectionRequest[],
    variables: ApiCollectionVariables,
  ): PostmanCollection {
    return {
      info: {
        name,
        schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      },
      auth: this.buildOAuth2Auth(variables),
      variable: [
        { key: 'baseUrl', value: baseUrl },
        { key: 'issuer', value: this.config.authConfig.issuer },
        { key: 'tokenEndpoint', value: this.config.authConfig.tokenEndpoint },
        { key: 'resource', value: resource },
        { key: 'clientId', value: 'Enter your clientId here' },
        { key: 'clientSecret', value: 'Enter your clientSecret here' },
      ],
      item: requests.map((request) => this.buildItem(request, variables)),
    };
  }

  private buildOAuth2Auth(variables: ApiCollectionVariables): PostmanAuth {
    return {
      type: 'oauth2',
      oauth2: [
        { key: 'grant_type', value: 'client_credentials', type: 'string' },
        { key: 'accessTokenUrl', value: variables.tokenEndpoint, type: 'string' },
        { key: 'scope', value: '.default', type: 'string' },
        {
          key: 'tokenRequestParams',
          value: [
            {
              key: 'resource',
              value: variables.resource,
              enabled: true,
              send_as: 'request_body',
            },
          ],
          type: 'any',
        },
        { key: 'clientId', value: variables.clientId, type: 'string' },
        { key: 'clientSecret', value: variables.clientSecret, type: 'string' },
        { key: 'client_authentication', value: 'header', type: 'string' },
        { key: 'addTokenTo', value: 'header', type: 'string' },
        { key: 'tokenType', value: 'Bearer', type: 'string' },
      ],
    };
  }

  private buildItem(request: OpenApiCollectionRequest, variables: ApiCollectionVariables): PostmanRequestItem {
    const headers = this.buildHeaders(request.body);
    const body = this.buildBody(request.body);

    return {
      name: request.name,
      request: {
        method: request.method,
        header: headers,
        url: this.buildUrl(request, variables),
        ...(body ? { body } : {}),
        ...(request.description ? { description: request.description } : {}),
      },
    };
  }

  private buildHeaders(body: OpenApiCollectionRequestBody): PostmanHeader[] {
    if (body.mode === 'none') {
      return [];
    }

    return [
      {
        key: 'Content-Type',
        value: body.contentType,
      },
    ];
  }

  private buildBody(body: OpenApiCollectionRequestBody): PostmanBody | undefined {
    if (body.mode === 'none') {
      return undefined;
    }

    if (body.mode === 'formUrlEncoded') {
      return {
        mode: 'urlencoded',
        urlencoded: body.parameters.map((parameter) => ({
          key: parameter.name,
          value: parameter.value,
          type: 'text',
        })),
      };
    }

    return {
      mode: 'raw',
      raw: body.value,
      options: {
        raw: {
          language: body.contentType.includes('json') ? 'json' : 'text',
        },
      },
    };
  }

  private buildUrl(request: OpenApiCollectionRequest, variables: ApiCollectionVariables): PostmanUrl {
    return {
      raw: `${variables.baseUrl}${request.path}${this.buildQueryString(request.queryParameters)}`,
      host: [variables.baseUrl],
      path: request.path.replace(/^\//, '').split('/').filter(Boolean),
      query: request.queryParameters.map((parameter) => ({
        key: parameter.name,
        value: parameter.value,
        ...(parameter.description ? { description: parameter.description } : {}),
      })),
    };
  }

  private buildQueryString(parameters: OpenApiCollectionParameter[]): string {
    if (parameters.length === 0) {
      return '';
    }

    const queryString = parameters.map((parameter) => `${parameter.name}=${parameter.value}`).join('&');

    return `?${queryString}`;
  }
}
